import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './components.css';
import Branding from './Branding';
import FeatureTiers from './FeatureTiers';
import posthog from '../utils/posthog-stub'; // FERPA: Using no-op stub until self-hosted PostHog is configured

const SubscriptionManagement = ({ user, onBack }) => {
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingPortal, setUpdatingPortal] = useState(false);
  const [activeTab, setActiveTab] = useState('subscription');
  
  // Account settings state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [emailForm, setEmailForm] = useState({
    email: user?.email || ''
  });
  const [deleteForm, setDeleteForm] = useState({
    password: '',
    confirmation: ''
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState('');

  const fetchSubscriptionData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/payment/subscription-status?user_id=${user.username}`);
      setSubscriptionData(response.data);
    } catch (error) {
      console.error('Error fetching subscription data:', error);
      // If API fails, we can still show user tier info based on their role
      setError('');
      setSubscriptionData(null);
    } finally {
      setLoading(false);
    }
  }, [user.username]);

  useEffect(() => {
    fetchSubscriptionData();
  }, [fetchSubscriptionData]);

  const openCustomerPortal = async () => {
    try {
      setUpdatingPortal(true);
      
      // Track portal access in PostHog
      try {
        posthog.capture('customer_portal_accessed', {
          user_id: user.id,
          subscription_tier: subscriptionData?.tier,
          has_subscription: subscriptionData?.has_subscription
        });
      } catch (err) {
        console.error('PostHog tracking error:', err);
      }

      const response = await axios.post('/api/payment/create-customer-portal-session', {
        user_id: user.username
      });

      // Redirect to Stripe Customer Portal
      window.location.href = response.data.portal_url;
    } catch (error) {
      console.error('Error creating customer portal session:', error);
      setError(error.response?.data?.error || 'Failed to open customer portal');
      setUpdatingPortal(false);
    }
  };

  const getTierDisplayName = (tier) => {
    const tierNames = {
      'student': 'Student',
      'premium': 'Premium',
      'expert': 'Expert',
      'expert-family': 'Expert Family'
    };
    return tierNames[tier] || tier;
  };

  const getTierDescription = (tier) => {
    const descriptions = {
      'guest': 'Free limited access',
      'student': 'Unlimited tutoring and progress tracking',
      'premium': 'Enhanced features with advanced visualizations',
      'expert': 'Full access with specialized features',
      'expert-family': 'Expert features for the whole family',
      'teacher': 'Educator account with management tools',
      'admin': 'Administrator account'
    };
    return descriptions[tier] || 'Custom subscription tier';
  };

  // Check if user is a family member (part of someone else's Home plan)
  const isFamilyMember = () => {
    return user?.family_plan_id != null;
  };

  // Get the user's actual tier and subscription status
  const getUserStatus = () => {
    const userTier = FeatureTiers.getUserTier(user);
    const isGuest = userTier === 'guest';
    const hasPaymentSubscription = subscriptionData?.has_subscription;
    const familyMember = isFamilyMember();

    return {
      tier: userTier,
      isGuest,
      hasPaymentSubscription,
      isPaidTier: !isGuest,
      displayName: familyMember ? 'Unlimited' : getTierDisplayName(userTier),
      description: familyMember ? 'Full unlimited access as part of a Home group' : getTierDescription(userTier),
      isFamilyMember: familyMember
    };
  };

  // Account settings functions
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setSettingsLoading(true);
    setSettingsMessage('');

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setSettingsMessage('New passwords do not match');
      setSettingsLoading(false);
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setSettingsMessage('Password must be at least 6 characters long');
      setSettingsLoading(false);
      return;
    }

    try {
      const response = await axios.post('/api/auth/change-password', {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      });

      if (response.data.success) {
        setSettingsMessage('Password changed successfully');
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        setSettingsMessage(response.data.error || 'Failed to change password');
      }
    } catch (error) {
      setSettingsMessage(error.response?.data?.error || 'An error occurred changing your password');
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleEmailUpdate = async (e) => {
    e.preventDefault();
    setSettingsLoading(true);
    setSettingsMessage('');

    try {
      const response = await axios.post('/api/auth/update-email', {
        email: emailForm.email
      });

      if (response.data.success) {
        setSettingsMessage('Email updated successfully');
      } else {
        setSettingsMessage(response.data.error || 'Failed to update email');
      }
    } catch (error) {
      setSettingsMessage(error.response?.data?.error || 'An error occurred updating your email');
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleAccountDeletion = async (e) => {
    e.preventDefault();
    setSettingsLoading(true);
    setSettingsMessage('');

    if (deleteForm.confirmation !== 'DELETE') {
      setSettingsMessage('Please type DELETE to confirm account deletion');
      setSettingsLoading(false);
      return;
    }

    if (!deleteForm.password) {
      setSettingsMessage('Password is required to delete your account');
      setSettingsLoading(false);
      return;
    }

    try {
      const response = await axios.post('/api/auth/delete-account', {
        password: deleteForm.password,
        confirmation: deleteForm.confirmation
      });

      if (response.data.success) {
        setSettingsMessage('Account deleted successfully. Redirecting...');
        setTimeout(() => {
          window.location.href = '/';
        }, 2000);
      } else {
        setSettingsMessage(response.data.error || 'Failed to delete account');
      }
    } catch (error) {
      setSettingsMessage(error.response?.data?.error || 'An error occurred deleting your account');
    } finally {
      setSettingsLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
        flexDirection: 'column'
      }}>
        <div style={{
          color: Branding.colors.deepPurple,
          fontSize: Branding.typography.fontSize.lg,
          marginBottom: Branding.spacing.md
        }}>Loading subscription information...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        backgroundColor: Branding.colors.pureWhite,
        borderRadius: Branding.borders.radius.lg,
        boxShadow: Branding.shadows.lg,
        padding: Branding.spacing.xl,
        maxWidth: '600px',
        margin: '0 auto'
      }}>
        <div style={{
          backgroundColor: '#fee2e2',
          border: '1px solid #ef4444',
          borderRadius: Branding.borders.radius.md,
          color: '#b91c1c',
          padding: Branding.spacing.md,
          marginBottom: Branding.spacing.lg,
          fontSize: Branding.typography.fontSize.sm,
          textAlign: 'center'
        }}>
          {error}
        </div>
        
        <div style={{ textAlign: 'center' }}>
          <button 
            onClick={onBack}
            style={{
              backgroundColor: 'transparent',
              border: `1px solid ${Branding.colors.tealBlue}`,
              color: Branding.colors.tealBlue,
              borderRadius: Branding.borders.radius.md,
              padding: `${Branding.spacing.sm} ${Branding.spacing.lg}`,
              fontSize: Branding.typography.fontSize.base,
              cursor: 'pointer',
              transition: Branding.transitions.default
            }}
          >
            ← Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: Branding.colors.pureWhite,
      borderRadius: Branding.borders.radius.lg,
      boxShadow: Branding.shadows.lg,
      padding: Branding.spacing.xl,
      maxWidth: '800px',
      margin: '0 auto'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: Branding.spacing.lg
      }}>
        <button 
          onClick={onBack}
          style={{
            background: 'none',
            border: 'none',
            color: Branding.colors.tealBlue,
            cursor: 'pointer',
            fontSize: Branding.typography.fontSize.lg,
            marginRight: Branding.spacing.md
          }}
        >
          ←
        </button>
        <h2 style={{
          color: Branding.colors.deepPurple,
          margin: 0,
          fontWeight: Branding.typography.fontWeight.bold,
          fontSize: Branding.typography.fontSize['2xl']
        }}>Account Settings</h2>
      </div>

      {/* Tab Navigation */}
      <div style={{
        borderBottom: `1px solid ${Branding.colors.offWhite}`,
        marginBottom: Branding.spacing.lg
      }}>
        <div style={{ display: 'flex', gap: Branding.spacing.md }}>
          <button
            onClick={() => setActiveTab('subscription')}
            style={{
              padding: `${Branding.spacing.sm} ${Branding.spacing.lg}`,
              border: 'none',
              background: 'none',
              color: activeTab === 'subscription' ? Branding.colors.deepPurple : Branding.colors.slateGray,
              borderBottom: activeTab === 'subscription' ? `2px solid ${Branding.colors.deepPurple}` : '2px solid transparent',
              cursor: 'pointer',
              fontWeight: Branding.typography.fontWeight.medium,
              fontSize: Branding.typography.fontSize.base
            }}
          >
            Subscription
          </button>
          <button
            onClick={() => setActiveTab('account')}
            style={{
              padding: `${Branding.spacing.sm} ${Branding.spacing.lg}`,
              border: 'none',
              background: 'none',
              color: activeTab === 'account' ? Branding.colors.deepPurple : Branding.colors.slateGray,
              borderBottom: activeTab === 'account' ? `2px solid ${Branding.colors.deepPurple}` : '2px solid transparent',
              cursor: 'pointer',
              fontWeight: Branding.typography.fontWeight.medium,
              fontSize: Branding.typography.fontSize.base
            }}
          >
            Account
          </button>
          <button
            onClick={() => setActiveTab('security')}
            style={{
              padding: `${Branding.spacing.sm} ${Branding.spacing.lg}`,
              border: 'none',
              background: 'none',
              color: activeTab === 'security' ? Branding.colors.deepPurple : Branding.colors.slateGray,
              borderBottom: activeTab === 'security' ? `2px solid ${Branding.colors.deepPurple}` : '2px solid transparent',
              cursor: 'pointer',
              fontWeight: Branding.typography.fontWeight.medium,
              fontSize: Branding.typography.fontSize.base
            }}
          >
            Security
          </button>
        </div>
      </div>

      {/* Settings Message */}
      {settingsMessage && (
        <div style={{
          backgroundColor: settingsMessage.includes('successfully') ? '#dcfce7' : '#fee2e2',
          border: `1px solid ${settingsMessage.includes('successfully') ? '#22c55e' : '#ef4444'}`,
          borderRadius: Branding.borders.radius.md,
          color: settingsMessage.includes('successfully') ? '#15803d' : '#b91c1c',
          padding: Branding.spacing.md,
          marginBottom: Branding.spacing.lg,
          fontSize: Branding.typography.fontSize.sm
        }}>
          {settingsMessage}
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'subscription' && (
        <div>
          {/* Account Information */}
          <div style={{
            backgroundColor: Branding.colors.veryLightGray,
            borderRadius: Branding.borders.radius.md,
            padding: Branding.spacing.lg,
            marginBottom: Branding.spacing.lg
          }}>
            <h3 style={{
              color: Branding.colors.deepPurple,
              marginTop: 0,
              marginBottom: Branding.spacing.md,
              fontWeight: Branding.typography.fontWeight.semibold,
              fontSize: Branding.typography.fontSize.lg
            }}>Account Information</h3>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: Branding.spacing.md,
              fontSize: Branding.typography.fontSize.sm
            }}>
              <div>
                <strong style={{ color: Branding.colors.darkSlate }}>Username:</strong>
                <div style={{ color: Branding.colors.slateGray }}>{user.username}</div>
              </div>
              <div>
                <strong style={{ color: Branding.colors.darkSlate }}>Email:</strong>
                <div style={{ color: Branding.colors.slateGray }}>{user.email || 'Not provided'}</div>
              </div>
            </div>
          </div>

          {/* Subscription Status */}
          <div style={{
            backgroundColor: getUserStatus().isPaidTier ? '#dcfce7' : '#fff7ed',
            border: `1px solid ${getUserStatus().isPaidTier ? '#22c55e' : '#f97316'}`,
            borderRadius: Branding.borders.radius.md,
            padding: Branding.spacing.lg,
            marginBottom: Branding.spacing.lg
          }}>
            <h3 style={{
              color: Branding.colors.deepPurple,
              marginTop: 0,
              marginBottom: Branding.spacing.md,
              fontWeight: Branding.typography.fontWeight.semibold,
              fontSize: Branding.typography.fontSize.lg
            }}>Subscription Status</h3>
            
            {getUserStatus().isPaidTier ? (
              <div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: Branding.spacing.sm
                }}>
                  <span style={{
                    backgroundColor: '#22c55e',
                    color: 'white',
                    padding: `${Branding.spacing.xs} ${Branding.spacing.sm}`,
                    borderRadius: Branding.borders.radius.sm,
                    fontSize: Branding.typography.fontSize.xs,
                    fontWeight: Branding.typography.fontWeight.semibold,
                    marginRight: Branding.spacing.md
                  }}>
                    {getUserStatus().hasPaymentSubscription ? (subscriptionData.status?.toUpperCase() || 'ACTIVE') : 'ACTIVE'}
                  </span>
                  <span style={{
                    color: Branding.colors.deepPurple,
                    fontWeight: Branding.typography.fontWeight.semibold,
                    fontSize: Branding.typography.fontSize.base
                  }}>
                    {getUserStatus().displayName}
                  </span>
                </div>
                
                <p style={{
                  color: Branding.colors.slateGray,
                  fontSize: Branding.typography.fontSize.sm,
                  margin: `${Branding.spacing.sm} 0`
                }}>
                  {getUserStatus().description}
                </p>
                
                {subscriptionData.current_period_end && (
                  <p style={{
                    color: Branding.colors.slateGray,
                    fontSize: Branding.typography.fontSize.sm,
                    margin: 0
                  }}>
                    <strong>Next billing date:</strong> {new Date(subscriptionData.current_period_end).toLocaleDateString()}
                  </p>
                )}
              </div>
            ) : (
              <div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: Branding.spacing.sm
                }}>
                  <span style={{
                    backgroundColor: getUserStatus().isGuest ? '#f97316' : '#22c55e',
                    color: 'white',
                    padding: `${Branding.spacing.xs} ${Branding.spacing.sm}`,
                    borderRadius: Branding.borders.radius.sm,
                    fontSize: Branding.typography.fontSize.xs,
                    fontWeight: Branding.typography.fontWeight.semibold,
                    marginRight: Branding.spacing.md
                  }}>
                    {getUserStatus().isGuest ? 'FREE' : 'ACTIVE'}
                  </span>
                  <span style={{
                    color: Branding.colors.deepPurple,
                    fontWeight: Branding.typography.fontWeight.semibold,
                    fontSize: Branding.typography.fontSize.base
                  }}>
                    {getUserStatus().displayName}
                  </span>
                </div>
                
                <p style={{
                  color: Branding.colors.slateGray,
                  fontSize: Branding.typography.fontSize.sm,
                  margin: 0
                }}>
                  {getUserStatus().isGuest ? 
                    "You're currently on the free tier. Upgrade to unlock more features!" : 
                    getUserStatus().description
                  }
                </p>
              </div>
            )}
          </div>

          {/* Family Member Notice - Show for users who are part of someone else's Home plan */}
          {getUserStatus().isFamilyMember && (
            <div style={{
              backgroundColor: '#e0f2fe',
              border: '1px solid #0284c7',
              borderRadius: Branding.borders.radius.md,
              padding: Branding.spacing.lg,
              marginBottom: Branding.spacing.lg
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: Branding.spacing.sm
              }}>
                <span style={{ fontSize: '24px', marginRight: Branding.spacing.sm }}>👨‍👩‍👧‍👦</span>
                <h3 style={{
                  color: '#0369a1',
                  margin: 0,
                  fontWeight: Branding.typography.fontWeight.semibold,
                  fontSize: Branding.typography.fontSize.lg
                }}>You're Part of a Home Group</h3>
              </div>
              <p style={{
                color: '#0c4a6e',
                fontSize: Branding.typography.fontSize.sm,
                margin: 0
              }}>
                Your account is managed by your group administrator. You have full unlimited access to all features.
                Contact your group admin if you have questions about your subscription.
              </p>
            </div>
          )}

          {/* Management Actions - Only show for users with payment subscriptions or guests (NOT family members) */}
          {!getUserStatus().isFamilyMember && (getUserStatus().hasPaymentSubscription || getUserStatus().isGuest) && (
            <div style={{
              borderTop: `1px solid ${Branding.colors.offWhite}`,
              paddingTop: Branding.spacing.lg
            }}>
              <h3 style={{
                color: Branding.colors.deepPurple,
                marginTop: 0,
                marginBottom: Branding.spacing.md,
                fontWeight: Branding.typography.fontWeight.semibold,
                fontSize: Branding.typography.fontSize.lg
              }}>Manage Your Subscription</h3>

              {getUserStatus().hasPaymentSubscription ? (
                <div>
                  <p style={{
                    color: Branding.colors.slateGray,
                    fontSize: Branding.typography.fontSize.sm,
                    marginBottom: Branding.spacing.lg
                  }}>
                    Use the Customer Portal to update your payment method, download invoices, or modify your subscription.
                  </p>

                  <button
                    onClick={openCustomerPortal}
                    disabled={updatingPortal}
                    style={{
                      width: '100%',
                      padding: Branding.spacing.md,
                      background: Branding.gradients.timerGradient,
                      color: Branding.colors.pureWhite,
                      border: 'none',
                      borderRadius: Branding.borders.radius.md,
                      fontSize: Branding.typography.fontSize.base,
                      fontWeight: Branding.typography.fontWeight.semibold,
                      cursor: updatingPortal ? 'not-allowed' : 'pointer',
                      transition: Branding.transitions.default,
                      boxShadow: Branding.shadows.md,
                      opacity: updatingPortal ? 0.8 : 1
                    }}
                  >
                    {updatingPortal ? 'Opening Customer Portal...' : 'Open Customer Portal'}
                  </button>

                  <div style={{
                    marginTop: Branding.spacing.md,
                    fontSize: Branding.typography.fontSize.xs,
                    color: Branding.colors.mutedMauve,
                    textAlign: 'center'
                  }}>
                    You'll be redirected to our secure billing portal where you can manage all aspects of your subscription.
                  </div>
                </div>
              ) : (
                <div>
                  <p style={{
                    color: Branding.colors.slateGray,
                    fontSize: Branding.typography.fontSize.sm,
                    marginBottom: Branding.spacing.lg
                  }}>
                    Ready to unlock unlimited access? Choose a subscription plan that works for you.
                  </p>

                  <button
                    onClick={() => window.location.href = '/upgrade'}
                    style={{
                      width: '100%',
                      padding: Branding.spacing.md,
                      background: Branding.gradients.warmGradient,
                      color: Branding.colors.pureWhite,
                      border: 'none',
                      borderRadius: Branding.borders.radius.md,
                      fontSize: Branding.typography.fontSize.base,
                      fontWeight: Branding.typography.fontWeight.semibold,
                      cursor: 'pointer',
                      transition: Branding.transitions.default,
                      boxShadow: Branding.shadows.md
                    }}
                  >
                    View Subscription Plans
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Account Tab */}
      {activeTab === 'account' && (
        <div>
          <h3 style={{
            color: Branding.colors.deepPurple,
            marginTop: 0,
            marginBottom: Branding.spacing.lg,
            fontWeight: Branding.typography.fontWeight.semibold,
            fontSize: Branding.typography.fontSize.lg
          }}>Account Information</h3>
          
          {/* Account Info Section */}
          <div style={{
            backgroundColor: Branding.colors.veryLightGray,
            borderRadius: Branding.borders.radius.md,
            padding: Branding.spacing.lg,
            marginBottom: Branding.spacing.lg
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: Branding.spacing.md,
              fontSize: Branding.typography.fontSize.sm
            }}>
              <div>
                <strong style={{ color: Branding.colors.darkSlate }}>Username:</strong>
                <div style={{ color: Branding.colors.slateGray }}>{user.username}</div>
              </div>
              <div>
                <strong style={{ color: Branding.colors.darkSlate }}>Current Email:</strong>
                <div style={{ color: Branding.colors.slateGray }}>{user.email || 'Not provided'}</div>
              </div>
            </div>
          </div>

          {/* Update Email Form */}
          <form onSubmit={handleEmailUpdate} style={{ marginBottom: Branding.spacing.lg }}>
            <h4 style={{
              color: Branding.colors.deepPurple,
              marginBottom: Branding.spacing.md,
              fontWeight: Branding.typography.fontWeight.semibold
            }}>Update Email Address</h4>
            
            <div style={{ marginBottom: Branding.spacing.md }}>
              <label style={{
                display: 'block',
                marginBottom: Branding.spacing.sm,
                color: Branding.colors.darkSlate,
                fontWeight: Branding.typography.fontWeight.medium
              }}>
                New Email Address
              </label>
              <input
                type="email"
                value={emailForm.email}
                onChange={(e) => setEmailForm({ email: e.target.value })}
                style={{
                  width: '100%',
                  padding: Branding.spacing.md,
                  border: `1px solid ${Branding.colors.veryLightGray}`,
                  borderRadius: Branding.borders.radius.md,
                  fontSize: Branding.typography.fontSize.base,
                  fontFamily: Branding.typography.fontFamily
                }}
                placeholder="Enter your new email address"
                required
              />
            </div>
            
            <button
              type="submit"
              disabled={settingsLoading}
              style={{
                padding: `${Branding.spacing.md} ${Branding.spacing.lg}`,
                background: Branding.gradients.timerGradient,
                color: Branding.colors.pureWhite,
                border: 'none',
                borderRadius: Branding.borders.radius.md,
                fontSize: Branding.typography.fontSize.base,
                fontWeight: Branding.typography.fontWeight.semibold,
                cursor: settingsLoading ? 'not-allowed' : 'pointer',
                transition: Branding.transitions.default,
                opacity: settingsLoading ? 0.8 : 1
              }}
            >
              {settingsLoading ? 'Updating...' : 'Update Email'}
            </button>
          </form>
        </div>
      )}

      {/* Security Tab */}
      {activeTab === 'security' && (
        <div>
          <h3 style={{
            color: Branding.colors.deepPurple,
            marginTop: 0,
            marginBottom: Branding.spacing.lg,
            fontWeight: Branding.typography.fontWeight.semibold,
            fontSize: Branding.typography.fontSize.lg
          }}>Security Settings</h3>

          {/* Change Password Form */}
          <form onSubmit={handlePasswordChange} style={{ marginBottom: Branding.spacing.xl }}>
            <h4 style={{
              color: Branding.colors.deepPurple,
              marginBottom: Branding.spacing.md,
              fontWeight: Branding.typography.fontWeight.semibold
            }}>Change Password</h4>
            
            <div style={{ marginBottom: Branding.spacing.md }}>
              <label style={{
                display: 'block',
                marginBottom: Branding.spacing.sm,
                color: Branding.colors.darkSlate,
                fontWeight: Branding.typography.fontWeight.medium
              }}>
                Current Password
              </label>
              <input
                type="password"
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                style={{
                  width: '100%',
                  padding: Branding.spacing.md,
                  border: `1px solid ${Branding.colors.veryLightGray}`,
                  borderRadius: Branding.borders.radius.md,
                  fontSize: Branding.typography.fontSize.base,
                  fontFamily: Branding.typography.fontFamily
                }}
                required
              />
            </div>
            
            <div style={{ marginBottom: Branding.spacing.md }}>
              <label style={{
                display: 'block',
                marginBottom: Branding.spacing.sm,
                color: Branding.colors.darkSlate,
                fontWeight: Branding.typography.fontWeight.medium
              }}>
                New Password
              </label>
              <input
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                style={{
                  width: '100%',
                  padding: Branding.spacing.md,
                  border: `1px solid ${Branding.colors.veryLightGray}`,
                  borderRadius: Branding.borders.radius.md,
                  fontSize: Branding.typography.fontSize.base,
                  fontFamily: Branding.typography.fontFamily
                }}
                minLength="6"
                required
              />
            </div>
            
            <div style={{ marginBottom: Branding.spacing.md }}>
              <label style={{
                display: 'block',
                marginBottom: Branding.spacing.sm,
                color: Branding.colors.darkSlate,
                fontWeight: Branding.typography.fontWeight.medium
              }}>
                Confirm New Password
              </label>
              <input
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                style={{
                  width: '100%',
                  padding: Branding.spacing.md,
                  border: `1px solid ${Branding.colors.veryLightGray}`,
                  borderRadius: Branding.borders.radius.md,
                  fontSize: Branding.typography.fontSize.base,
                  fontFamily: Branding.typography.fontFamily
                }}
                required
              />
            </div>
            
            <button
              type="submit"
              disabled={settingsLoading}
              style={{
                padding: `${Branding.spacing.md} ${Branding.spacing.lg}`,
                background: Branding.gradients.timerGradient,
                color: Branding.colors.pureWhite,
                border: 'none',
                borderRadius: Branding.borders.radius.md,
                fontSize: Branding.typography.fontSize.base,
                fontWeight: Branding.typography.fontWeight.semibold,
                cursor: settingsLoading ? 'not-allowed' : 'pointer',
                transition: Branding.transitions.default,
                opacity: settingsLoading ? 0.8 : 1
              }}
            >
              {settingsLoading ? 'Changing...' : 'Change Password'}
            </button>
          </form>

          {/* Delete Account Section */}
          <div style={{
            backgroundColor: '#fee2e2',
            border: '1px solid #ef4444',
            borderRadius: Branding.borders.radius.md,
            padding: Branding.spacing.lg
          }}>
            <h4 style={{
              color: '#b91c1c',
              marginTop: 0,
              marginBottom: Branding.spacing.md,
              fontWeight: Branding.typography.fontWeight.semibold
            }}>Delete Account</h4>
            
            <p style={{
              color: '#7f1d1d',
              marginBottom: Branding.spacing.lg,
              fontSize: Branding.typography.fontSize.sm
            }}>
              Warning: This action is permanent and cannot be undone. All your data will be deleted.
              {getUserStatus().hasPaymentSubscription && (
                <span style={{ display: 'block', marginTop: Branding.spacing.sm, fontWeight: 'bold' }}>
                  Your active subscription will also be canceled immediately.
                </span>
              )}
            </p>
            
            <form onSubmit={handleAccountDeletion}>
              <div style={{ marginBottom: Branding.spacing.md }}>
                <label style={{
                  display: 'block',
                  marginBottom: Branding.spacing.sm,
                  color: '#7f1d1d',
                  fontWeight: Branding.typography.fontWeight.medium
                }}>
                  Password
                </label>
                <input
                  type="password"
                  value={deleteForm.password}
                  onChange={(e) => setDeleteForm(prev => ({ ...prev, password: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: Branding.spacing.md,
                    border: `1px solid #ef4444`,
                    borderRadius: Branding.borders.radius.md,
                    fontSize: Branding.typography.fontSize.base,
                    fontFamily: Branding.typography.fontFamily
                  }}
                  required
                />
              </div>
              
              <div style={{ marginBottom: Branding.spacing.md }}>
                <label style={{
                  display: 'block',
                  marginBottom: Branding.spacing.sm,
                  color: '#7f1d1d',
                  fontWeight: Branding.typography.fontWeight.medium
                }}>
                  Type "DELETE" to confirm
                </label>
                <input
                  type="text"
                  value={deleteForm.confirmation}
                  onChange={(e) => setDeleteForm(prev => ({ ...prev, confirmation: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: Branding.spacing.md,
                    border: `1px solid #ef4444`,
                    borderRadius: Branding.borders.radius.md,
                    fontSize: Branding.typography.fontSize.base,
                    fontFamily: Branding.typography.fontFamily
                  }}
                  placeholder="DELETE"
                  required
                />
              </div>
              
              <button
                type="submit"
                disabled={settingsLoading}
                style={{
                  padding: `${Branding.spacing.md} ${Branding.spacing.lg}`,
                  backgroundColor: '#ef4444',
                  color: Branding.colors.pureWhite,
                  border: 'none',
                  borderRadius: Branding.borders.radius.md,
                  fontSize: Branding.typography.fontSize.base,
                  fontWeight: Branding.typography.fontWeight.semibold,
                  cursor: settingsLoading ? 'not-allowed' : 'pointer',
                  transition: Branding.transitions.default,
                  opacity: settingsLoading ? 0.8 : 1
                }}
              >
                {settingsLoading ? 'Deleting...' : 'Delete Account'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionManagement;